import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useHealthMode } from '../context/HealthModeContext';
import { useDashboard } from '../context/DashboardContext';
import { useOfflineSync } from '../hooks/useOfflineSync';
import { useAchievements } from '../hooks/useAchievements';
import { usePWA } from '../hooks/usePWA';
import { useBiometricAuth } from '../hooks/useBiometricAuth';
// import { useAppUpdate } from '../context/AppUpdateContext'; // ELIMINADO
import { headachesAPI, tensionsAPI, llorosAPI } from '../services/api';
import { Headache, HeadacheStats, HeadacheFormData, Tension, TensionStats, TensionFormData, Achievement, Lloro, LloroFormData, LloroStats } from '../types';
import HeadacheForm from '../components/HeadacheForm';
import TensionForm from '../components/TensionForm';
import LloroForm from '../components/LloroForm';
import { DashboardStats, QuickActions, TrendChart } from '../components/Dashboard';
import Calendar from '../components/Calendar/Calendar';
import EpisodeFilters from '../components/EpisodeFilters';
import OfflineIndicator from '../components/OfflineIndicator';
import AchievementsPanel from '../components/AchievementsPanel';
import AchievementNotification from '../components/AchievementNotification';
import BiometricSetup from '../components/BiometricSetup';
import AdvancedDashboard from '../components/AdvancedDashboard';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import './Dashboard.css';

// Función helper para extraer hora de fecha sin conversión de timezone
// La fecha viene del backend como "2024-12-29 01:46:00" o "2024-12-29T01:46:00"
const extractTimeFromDate = (dateStr: string): string => {
  if (!dateStr) return '--:--';
  try {
    // Buscar el patrón HH:MM en la cadena
    const timeMatch = dateStr.match(/(\d{2}):(\d{2})/);
    if (timeMatch) {
      return `${timeMatch[1]}:${timeMatch[2]}`;
    }
    return '--:--';
  } catch {
    return '--:--';
  }
};

// Función helper para iconos de ubicación
const getLocationIcon = (location: string) => {
  switch (location) {
    case 'Frente':
      return '👁️';
    case 'Sien':
      return '⚡';
    case 'Parte Superior':
      return '⬆️';
    case 'Nuca':
      return '🔙';
    case 'Toda la Cabeza':
      return '🤕';
    default:
      return '📍';
  }
};

// Función para formatear ubicación con iconos
const formatLocationWithIcons = (location: string) => {
  if (!location) return '';
  
  // Dividir por coma con o sin espacio para manejar ambos formatos
  const locations = location.split(/,\s*/);
  return locations.map(loc => `${getLocationIcon(loc.trim())} ${loc.trim()}`).join(', ');
};

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { healthMode, canUseLloros } = useHealthMode();
  const { currentView } = useDashboard();
  const { isOnline, addOfflineData, syncPendingData } = useOfflineSync();
  const { stats: achievementStats, processNewAchievements } = useAchievements();
  const { isPWA } = usePWA();
  const { isAvailable, hasCredentials } = useBiometricAuth();
  // const { canShowBiometric } = useAppUpdate(); // ELIMINADO: Sistema simplificado
  const canShowBiometric = true; // Siempre permitir biométrico
  const [headaches, setHeadaches] = useState<Headache[]>([]);
  const [tensions, setTensions] = useState<Tension[]>([]);
  const [lloros, setLloros] = useState<Lloro[]>([]);
  const [stats, setStats] = useState<HeadacheStats | null>(null);
  const [tensionStats, setTensionStats] = useState<TensionStats | null>(null);
  const [lloroStats, setLloroStats] = useState<LloroStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [editingHeadache, setEditingHeadache] = useState<Headache | null>(null);
  const [editingTension, setEditingTension] = useState<Tension | null>(null);
  const [editingLloro, setEditingLloro] = useState<Lloro | null>(null);
  const [selectedDayTensions, setSelectedDayTensions] = useState<Tension[]>([]);
  const [selectedDayLloros, setSelectedDayLloros] = useState<Lloro[]>([]);
  const [showDayDetail, setShowDayDetail] = useState(false);
  const [error, setError] = useState('');
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isExportingAnnualPDF, setIsExportingAnnualPDF] = useState(false);
  const [showAchievements, setShowAchievements] = useState(false);
  const [achievementNotifications, setAchievementNotifications] = useState<Achievement[]>([]);
  const [currentCalendarDate, setCurrentCalendarDate] = useState(new Date());
  const [showBiometricSetup, setShowBiometricSetup] = useState(false);
  const [biometricSetupSeen, setBiometricSetupSeen] = useState(false);
  const calendarRef = useRef<HTMLDivElement>(null);
  
  // Estados para filtros de episodios
  const [episodeFilters, setEpisodeFilters] = useState({
    intensities: [] as string[],
    locations: [] as string[],
    dateFrom: '',
    dateTo: ''
  });

  // Navigation handled by DashboardContext

  useEffect(() => {
    loadDashboardData();
  }, []);

  // Handle view changes from header
  useEffect(() => {
    // Close any open modals when view changes
    setShowForm(false);
    setShowDayDetail(false);
    setShowAchievements(false);
  }, [currentView]);

  // Setup biométrico - Solo mostrar si no hay actualizaciones pendientes
  useEffect(() => {
    // Solo mostrar Face ID si:
    // 1. Está en PWA
    // 2. La biometría está disponible
    // 3. No tiene credenciales configuradas
    // 4. No ha visto el setup antes
    // 5. Se puede mostrar biometría (no hay actualizaciones pendientes)
    if (isPWA && 
        isAvailable && 
        !hasCredentials && 
        !biometricSetupSeen && 
        canShowBiometric) {
      
      // Mostrar después de 3 segundos para no interrumpir la carga
      const timer = setTimeout(() => {
        setShowBiometricSetup(true);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isPWA, isAvailable, hasCredentials, biometricSetupSeen, canShowBiometric]);

  // Bloquear scroll del body cuando hay modal abierto
  useEffect(() => {
    if (showForm || showDayDetail) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup al desmontar
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showForm, showDayDetail]);

  // Cerrar modal con tecla ESC
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && (showForm || showDayDetail)) {
        // TODO: Implementar check de cambios pendientes antes de cerrar
        closeAllModals();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showForm, showDayDetail]);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      const [headachesResponse, statsResponse] = await Promise.all([
        headachesAPI.getAll({ limit: 100 }), // Más registros para el calendario
        headachesAPI.getStats()
      ]);

      setHeadaches(headachesResponse.data.data.headaches);
      setStats(statsResponse.data.data.stats);
      
      
      // Cargar tensiones y estadísticas
      try {
        const [tensionsResponse, tensionStatsResponse] = await Promise.all([
          tensionsAPI.getAll({ limit: 100 }),
          tensionsAPI.getStats()
        ]);

        setTensions(tensionsResponse.data.data.tensions);
        setTensionStats(tensionStatsResponse.data.data.stats);
      } catch (tensionError) {
        console.error('Error loading tension data:', tensionError);
        // No mostrar error, solo mantener datos de tensión como vacíos
      }

      // Cargar lloros si el usuario tiene acceso (solo Anabel)
      if (canUseLloros) {
        try {
          const [llorosResponse, lloroStatsResponse] = await Promise.all([
            llorosAPI.getAll({ limit: 100 }),
            llorosAPI.getStats()
          ]);

          setLloros(llorosResponse.data.data.lloros);
          setLloroStats(lloroStatsResponse.data.data.stats);
        } catch (lloroError) {
          console.error('Error loading lloro data:', lloroError);
          // No mostrar error, solo mantener datos de lloros como vacíos
        }
      }
    } catch (err: any) {
      setError('Error al cargar los datos del dashboard');
      console.error('Error loading dashboard:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleHeadacheSubmit = async (data: HeadacheFormData) => {
    try {
      let response;
      
      if (editingHeadache) {
        // Actualizar registro existente (solo online)
        if (!isOnline) {
          throw new Error('No es posible editar registros sin conexión. Conéctate a internet para continuar.');
        }
        response = await headachesAPI.update(editingHeadache.id, data);
        const updatedHeadache = response.data.data.headache;
        setHeadaches(prev => prev.map(h => h.id === editingHeadache.id ? updatedHeadache : h));
      } else {
        // Crear nuevo registro - usar la fecha completa del formulario (incluye hora)
        const submitData = data;

        if (isOnline) {
          // Modo online: enviar directamente
          response = await headachesAPI.create(submitData);
          const newHeadache = response.data.data.headache;
          setHeadaches(prev => [newHeadache, ...prev]);
          
          // Recargar estadísticas
          const statsResponse = await headachesAPI.getStats();
          setStats(statsResponse.data.data.stats);
        } else {
          // Modo offline: guardar localmente
          const offlineId = addOfflineData('headache', submitData);
          
          // Crear un registro temporal para mostrar en la UI
          const tempHeadache: Headache & { __offline?: boolean } = {
            id: parseInt(offlineId.replace('offline_', '')),
            user_id: user?.id || 0,
            intensity: submitData.intensity as 'leve' | 'moderada' | 'fuerte',
            location: submitData.location,
            medication_taken: submitData.medication_taken || false,
            medication: submitData.medication,
            medication_time: submitData.medication_time,
            date: submitData.date || new Date().toISOString(),
            created_at: new Date().toISOString(),
            follow_up_sent_at: undefined,
            follow_up_response: 'pending',
            follow_up_responded_at: undefined,
            follow_up_token: undefined,
            follow_up_sent: false,
            __offline: true // Marcar como offline
          };
          
          setHeadaches(prev => [tempHeadache, ...prev]);
          
          // Mostrar mensaje de confirmación offline
          console.log('📴 Dolor de cabeza guardado offline - se sincronizará cuando vuelva la conexión');
        }
      }
      
      // Cerrar modales
      setShowForm(false);
      setShowDayDetail(false);
      setEditingHeadache(null);
      setSelectedDate('');
      
    } catch (err: any) {
      throw new Error(err.response?.data?.message || err.message || 'Error al guardar dolor de cabeza');
    }
  };



  const handleHeadacheDelete = async (id: number) => {
    try {
      await headachesAPI.delete(id);
      setHeadaches(prev => prev.filter(h => h.id !== id));
      
      // Recargar estadísticas
      const statsResponse = await headachesAPI.getStats();
      setStats(statsResponse.data.data.stats);
      
      // Cerrar modales
      setShowDayDetail(false);
      setEditingHeadache(null);
    } catch (err: any) {
      setError('Error al eliminar dolor de cabeza');
    }
  };

  const handleTensionDelete = async (id: number) => {
    try {
      await tensionsAPI.delete(id);
      setTensions(prev => prev.filter(t => t.id !== id));

      // Actualizar tensiones del día seleccionado
      setSelectedDayTensions(prev => prev.filter(t => t.id !== id));

      // Si ya no hay tensiones del día, cerrar el modal
      const remainingTensions = selectedDayTensions.filter(t => t.id !== id);
      if (remainingTensions.length === 0) {
        setShowDayDetail(false);
        setEditingTension(null);
        setSelectedDayTensions([]);
      }

      // Recargar estadísticas de tensiones
      try {
        const tensionStatsResponse = await tensionsAPI.getStats();
        setTensionStats(tensionStatsResponse.data.data.stats);
      } catch (statsError) {
        console.warn('Error loading tension stats after delete:', statsError);
      }
    } catch (err: any) {
      setError('Error al eliminar tensión');
    }
  };

  // Funciones para manejar lloros
  const handleLloroSubmit = async (data: LloroFormData) => {
    try {
      let response;

      if (editingLloro) {
        // Actualizar registro existente
        if (!isOnline) {
          throw new Error('No es posible editar registros sin conexión. Conéctate a internet para continuar.');
        }
        response = await llorosAPI.update(editingLloro.id, data);
        const updatedLloro = response.data.data.lloro;
        setLloros(prev => prev.map(l => l.id === editingLloro.id ? updatedLloro : l));
      } else {
        // Crear nuevo registro - usar la fecha del formulario
        const submitData = data;

        if (isOnline) {
          response = await llorosAPI.create(submitData);
          const newLloro = response.data.data.lloro;
          setLloros(prev => [newLloro, ...prev]);

          // Recargar estadísticas
          const lloroStatsResponse = await llorosAPI.getStats();
          setLloroStats(lloroStatsResponse.data.data.stats);
        } else {
          throw new Error('No es posible crear registros sin conexión.');
        }
      }

      // Cerrar modal y limpiar estado
      setShowForm(false);
      setEditingLloro(null);
      setSelectedDate('');
      setError('');
    } catch (err: any) {
      setError(err.message || 'Error al guardar el lloro');
    }
  };

  const handleLloroDelete = async (id: number) => {
    try {
      await llorosAPI.delete(id);
      setLloros(prev => prev.filter(l => l.id !== id));

      // Actualizar lloros del día seleccionado
      setSelectedDayLloros(prev => prev.filter(l => l.id !== id));

      // Si ya no hay lloros del día, cerrar el modal
      const remainingLloros = selectedDayLloros.filter(l => l.id !== id);
      if (remainingLloros.length === 0) {
        setShowDayDetail(false);
        setEditingLloro(null);
        setSelectedDayLloros([]);
      }

      // Recargar estadísticas de lloros
      try {
        const lloroStatsResponse = await llorosAPI.getStats();
        setLloroStats(lloroStatsResponse.data.data.stats);
      } catch (statsError) {
        console.warn('Error loading lloro stats after delete:', statsError);
      }
    } catch (err: any) {
      setError('Error al eliminar lloro');
    }
  };

  const handleDateClick = (date: string, existingItem?: Headache | Tension | Tension[] | Lloro | Lloro[]) => {
    setSelectedDate(date);

    if (existingItem) {
      if (healthMode === 'headaches') {
        setEditingHeadache(existingItem as Headache);
        setEditingTension(null);
        setEditingLloro(null);
        setSelectedDayTensions([]);
        setSelectedDayLloros([]);
        setShowDayDetail(true);
      } else if (healthMode === 'tensions') {
        // Manejo para tensiones (puede ser una tensión o array de tensiones)
        if (Array.isArray(existingItem)) {
          setSelectedDayTensions(existingItem as Tension[]);
          setEditingTension(null);
          setEditingHeadache(null);
          setEditingLloro(null);
          setSelectedDayLloros([]);
          setShowDayDetail(true);
        } else {
          setEditingTension(existingItem as Tension);
          setSelectedDayTensions([existingItem as Tension]);
          setEditingHeadache(null);
          setEditingLloro(null);
          setSelectedDayLloros([]);
          setShowDayDetail(true);
        }
      } else if (healthMode === 'lloros') {
        // Manejo para lloros
        if (Array.isArray(existingItem)) {
          setSelectedDayLloros(existingItem as Lloro[]);
          setEditingLloro(null);
          setEditingHeadache(null);
          setEditingTension(null);
          setSelectedDayTensions([]);
          setShowDayDetail(true);
        } else {
          setEditingLloro(existingItem as Lloro);
          setSelectedDayLloros([existingItem as Lloro]);
          setEditingHeadache(null);
          setEditingTension(null);
          setSelectedDayTensions([]);
          setShowDayDetail(true);
        }
      }
    } else {
      setEditingHeadache(null);
      setEditingTension(null);
      setEditingLloro(null);
      setSelectedDayTensions([]);
      setSelectedDayLloros([]);
      setShowForm(true);
    }
  };

  const handleNewHeadache = () => {
    setSelectedDate('');
    setEditingHeadache(null);
    setEditingTension(null);
    setEditingLloro(null);
    setShowForm(true);
  };

  const handleNewTension = () => {
    setSelectedDate('');
    setEditingHeadache(null);
    setEditingTension(null);
    setEditingLloro(null);
    setShowForm(true);
  };

  const handleNewLloro = () => {
    setSelectedDate('');
    setEditingHeadache(null);
    setEditingTension(null);
    setEditingLloro(null);
    setShowForm(true);
  };


  const handleExportTensionPDF = async () => {
    if (!calendarRef.current) return;
    
    setIsExportingPDF(true);
    
    try {
      // Crear PDF para tensiones en formato horizontal
      const pdf = new jsPDF('l', 'mm', 'a4'); // 'l' para landscape (horizontal)
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Configurar colores
      const primaryColor: [number, number, number] = [16, 185, 129]; // Verde para tensiones
      const secondaryColor: [number, number, number] = [100, 116, 139];

      // Obtener datos del mes actual
      const currentDate = currentCalendarDate;
      const monthYear = currentDate.toLocaleDateString('es-ES', { 
        month: 'long', 
        year: 'numeric' 
      });
      
      // Filtrar tensiones del mes actual
      const currentMonthTensions = tensions.filter(tension => {
        const tensionDate = new Date(tension.date);
        return tensionDate.getMonth() === currentDate.getMonth() &&
               tensionDate.getFullYear() === currentDate.getFullYear();
      });

      // Header compacto
      pdf.setFillColor(...primaryColor);
      pdf.rect(0, 0, pageWidth, 35, 'F');
      
      // Mes/año centrado
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      const subtitleText = monthYear.charAt(0).toUpperCase() + monthYear.slice(1);
      const subtitleWidth = pdf.getTextWidth(subtitleText);
      pdf.text(subtitleText, (pageWidth - subtitleWidth) / 2, 22);

      // Información del usuario a la izquierda
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Usuario: ${user?.name || 'Usuario'}`, 15, 15);
      pdf.text(`Generado: ${new Date().toLocaleDateString('es-ES')}`, 15, 28);
      
      // Información estadística a la derecha
      pdf.text(`Total: ${currentMonthTensions.length} registros`, pageWidth - 90, 15);
      if (currentMonthTensions.length > 0) {
        const avgSystolic = Math.round(currentMonthTensions.reduce((sum, t) => sum + t.systolic, 0) / currentMonthTensions.length);
        const avgDiastolic = Math.round(currentMonthTensions.reduce((sum, t) => sum + t.diastolic, 0) / currentMonthTensions.length);
        pdf.text(`Promedio: ${avgSystolic}/${avgDiastolic} mmHg`, pageWidth - 90, 28);
      }

      // Lista de registros en formato de pills
      if (currentMonthTensions.length > 0) {
        // Ordenar por fecha y hora (del día 1 al 30 del mes, y por hora dentro del día)
        const sortedTensions = [...currentMonthTensions].sort((a, b) => {
          const dateA = new Date(a.date + ' ' + (a.time || '00:00'));
          const dateB = new Date(b.date + ' ' + (b.time || '00:00'));
          return dateA.getTime() - dateB.getTime();
        });
        
        // Configuración para layout en grid - maximizar espacio
        const pillWidth = 85;  // Ancho de cada pill
        const pillHeight = 28; // Alto de cada pill (más compacto)
        const marginX = 8;     // Margen horizontal entre pills
        const marginY = 4;     // Margen vertical entre pills (más compacto)
        const startX = 15;     // Posición X inicial
        const startY = 45;     // Posición Y inicial (ajustado al nuevo header más pequeño)
        const pillsPerRow = 3; // 3 pills por fila
        
        // Función para categorizar tensión
        const getTensionCategory = (systolic: number, diastolic: number) => {
          if (systolic < 120 && diastolic < 80) return { name: 'Normal', color: [16, 185, 129] };
          if (systolic >= 120 && systolic <= 129 && diastolic < 80) return { name: 'Elevada', color: [245, 158, 11] };
          if ((systolic >= 130 && systolic <= 139) || (diastolic >= 80 && diastolic <= 89)) return { name: 'Alta', color: [249, 115, 22] };
          if (systolic >= 140 || diastolic >= 90) return { name: 'Muy Alta', color: [239, 68, 68] };
          return { name: 'Indeterminada', color: [107, 114, 128] };
        };
        
        // Calcular cuántas filas caben en una página - optimizar espacio
        const availableHeight = pageHeight - startY - 15; // Reducir margen inferior
        const rowsPerPage = Math.max(1, Math.floor(availableHeight / (pillHeight + marginY)));
        const pillsPerPage = rowsPerPage * pillsPerRow;
        
        console.log('PDF Debug:', { 
          pageWidth, 
          pageHeight, 
          availableHeight, 
          rowsPerPage, 
          pillsPerPage,
          startX,
          startY,
          pillWidth,
          pillHeight,
          marginX,
          marginY
        });
        
        sortedTensions.forEach((tension, index) => {
          // Verificar si necesitamos nueva página
          if (index > 0 && index % pillsPerPage === 0) {
            pdf.addPage();
          }
          
          // Calcular posición en grid dentro de la página actual
          const indexInPage = index % pillsPerPage;
          const row = Math.floor(indexInPage / pillsPerRow);
          const col = indexInPage % pillsPerRow;
          
          const xPos = startX + col * (pillWidth + marginX);
          const yPos = startY + row * (pillHeight + marginY);
          
          // Validar que las coordenadas sean válidas
          if (isNaN(xPos) || isNaN(yPos) || xPos < 0 || yPos < 0) {
            console.error('Invalid coordinates:', { xPos, yPos, col, row, index });
            return;
          }
          
          const category = getTensionCategory(tension.systolic, tension.diastolic);
          const date = new Date(tension.date);
          const dateStr = date.toLocaleDateString('es-ES', { 
            day: 'numeric', 
            month: 'short'
          });
          const timeStr = tension.time || '00:00';
          
          // Fondo de la pill
          pdf.setFillColor(248, 250, 252);
          pdf.rect(xPos, yPos, pillWidth, pillHeight, 'F');
          
          // Borde izquierdo colorizado según categoría
          pdf.setFillColor(...(category.color as [number, number, number]));
          pdf.rect(xPos, yPos, 2, pillHeight, 'F');
          
          // Fecha y hora (línea superior)
          pdf.setTextColor(0, 0, 0);
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(9);
          pdf.text(`${dateStr}, ${timeStr}`, xPos + 3, yPos + 7);

          // Valores de tensión (línea principal)
          pdf.setTextColor(...(category.color as [number, number, number]));
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(11);
          pdf.text(`${tension.systolic}/${tension.diastolic}`, xPos + 3, yPos + 16);

          // Solo pulsaciones (línea inferior) - ajustado para pill más pequeña
          if (tension.pulse) {
            pdf.setTextColor(0, 0, 0);
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(9);
            pdf.text(`${tension.pulse} pulsaciones`, xPos + 3, yPos + 24);
          }
        });
      }

      // Descargar PDF
      const fileName = `reporte-tensiones-maldolor-${monthYear.replace(' ', '-')}.pdf`;
      pdf.save(fileName);
      
    } catch (error) {
      console.error('Error generating tension PDF:', error);
      setError('Error al generar el reporte de tensiones');
    } finally {
      setIsExportingPDF(false);
    }
  };

  const handleExportTensionAnnualPDF = async () => {
    setIsExportingAnnualPDF(true);
    
    try {
      // Crear PDF anual para tensiones
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      
      // Configurar colores - Verde para tensiones
      const primaryColor: [number, number, number] = [16, 185, 129];
      
      // Obtener año actual
      const currentYear = currentCalendarDate.getFullYear();
      
      // Filtrar tensiones del año actual
      const currentYearTensions = tensions.filter(tension => {
        const tensionDate = new Date(tension.date);
        return tensionDate.getFullYear() === currentYear;
      });

      // Header principal
      pdf.setFillColor(...primaryColor);
      pdf.rect(0, 0, pageWidth, 50, 'F');
      
      // Título principal
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      const titleText = 'Reporte Anual de Tensión Arterial';
      const titleWidth = pdf.getTextWidth(titleText);
      pdf.text(titleText, (pageWidth - titleWidth) / 2, 25);
      
      // Año
      pdf.setFontSize(18);
      const yearText = currentYear.toString();
      const yearWidth = pdf.getTextWidth(yearText);
      pdf.text(yearText, (pageWidth - yearWidth) / 2, 40);

      // Información del paciente
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Usuario: ${user?.name || 'N/A'}`, 20, 70);
      pdf.text(`Total de registros en ${currentYear}: ${currentYearTensions.length}`, 20, 80);

      if (currentYearTensions.length > 0) {
        const avgSystolic = Math.round(currentYearTensions.reduce((sum, t) => sum + t.systolic, 0) / currentYearTensions.length);
        const avgDiastolic = Math.round(currentYearTensions.reduce((sum, t) => sum + t.diastolic, 0) / currentYearTensions.length);
        
        pdf.text(`Promedio sistólica anual: ${avgSystolic} mmHg`, 20, 90);
        pdf.text(`Promedio diastólica anual: ${avgDiastolic} mmHg`, 20, 100);
        
        // Análisis por categorías
        const categories = {
          normal: currentYearTensions.filter(t => t.systolic < 120 && t.diastolic < 80).length,
          elevada: currentYearTensions.filter(t => t.systolic >= 120 && t.systolic <= 129 && t.diastolic < 80).length,
          hipertension1: currentYearTensions.filter(t => (t.systolic >= 130 && t.systolic <= 139) || (t.diastolic >= 80 && t.diastolic <= 89)).length,
          hipertension2: currentYearTensions.filter(t => t.systolic >= 140 || t.diastolic >= 90).length
        };
        
        pdf.text('Clasificación de registros:', 20, 120);
        pdf.text(`• Normal: ${categories.normal} registros`, 25, 130);
        pdf.text(`• Elevada: ${categories.elevada} registros`, 25, 140);
        pdf.text(`• Hipertensión Grado 1: ${categories.hipertension1} registros`, 25, 150);
        pdf.text(`• Hipertensión Grado 2: ${categories.hipertension2} registros`, 25, 160);
      }

      // Descargar PDF
      const fileName = `reporte-anual-tensiones-${currentYear}.pdf`;
      pdf.save(fileName);
      
    } catch (error) {
      console.error('Error generating annual tension PDF:', error);
      setError('Error al generar el reporte anual de tensiones');
    } finally {
      setIsExportingAnnualPDF(false);
    }
  };

  const handleTensionSubmit = async (data: TensionFormData) => {
    try {
      let response;
      
      if (editingTension) {
        // Actualizar registro existente (solo online)
        if (!isOnline) {
          throw new Error('No es posible editar registros sin conexión. Conéctate a internet para continuar.');
        }
        response = await tensionsAPI.update(editingTension.id, data);
        const updatedTension = response.data.data.tension;
        setTensions(prev => prev.map(t => t.id === editingTension.id ? updatedTension : t));
      } else {
        // Crear nuevo registro - usar la fecha del formulario
        const submitData = data;

        if (isOnline) {
          // Modo online: enviar directamente
          response = await tensionsAPI.create(submitData);
          const newTension = response.data.data.tension;
          setTensions(prev => [newTension, ...prev]);
          
          // Procesar nuevos achievements si los hay
          if (response.data.data.new_achievements && response.data.data.new_achievements.length > 0) {
            processNewAchievements(response.data.data.new_achievements);
            setAchievementNotifications(response.data.data.new_achievements);
          }
          
          // Recargar estadísticas
          const tensionStatsResponse = await tensionsAPI.getStats();
          setTensionStats(tensionStatsResponse.data.data.stats);
        } else {
          // Modo offline: guardar localmente
          const offlineId = addOfflineData('tension', submitData);
          
          // Crear un registro temporal para mostrar en la UI
          const tempTension: Tension & { __offline?: boolean } = {
            id: parseInt(offlineId.replace('offline_', '')),
            user_id: user?.id || 0,
            systolic: typeof submitData.systolic === 'string' ? parseInt(submitData.systolic) : submitData.systolic,
            diastolic: typeof submitData.diastolic === 'string' ? parseInt(submitData.diastolic) : submitData.diastolic,
            pulse: submitData.pulse ? (typeof submitData.pulse === 'string' ? parseInt(submitData.pulse) : submitData.pulse) : undefined,
            notes: submitData.notes,
            date: submitData.date || new Date().toISOString().split('T')[0],
            time: submitData.time || new Date().toTimeString().split(' ')[0].substring(0, 5),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            __offline: true // Marcar como offline
          };
          
          setTensions(prev => [tempTension, ...prev]);
          
          // Mostrar mensaje de confirmación offline
          console.log('📴 Tensión guardada offline - se sincronizará cuando vuelva la conexión');
        }
      }
      
      // Cerrar modales
      setShowForm(false);
      setShowDayDetail(false);
      setEditingTension(null);
      setSelectedDate('');
      
    } catch (err: any) {
      throw new Error(err.response?.data?.message || err.message || 'Error al guardar tensión');
    }
  };

  const handleEditFromDetail = () => {
    setShowDayDetail(false);
    setShowForm(true);
  };

  // Funciones para achievements
  const handleAchievementNotificationClose = (achievementId: number) => {
    setAchievementNotifications(prev =>
      prev.filter(achievement => achievement.id !== achievementId)
    );
  };

  const handleOpenAchievements = () => {
    setShowAchievements(true);
  };

  const handleCloseAchievements = () => {
    setShowAchievements(false);
  };

  const handleExportPDF = async () => {
    if (!calendarRef.current) return;
    
    setIsExportingPDF(true);
    
    try {
      // Capturar el elemento del calendario
      const canvas = await html2canvas(calendarRef.current, {
        scale: 2, // Mayor calidad
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: calendarRef.current.scrollWidth,
        height: calendarRef.current.scrollHeight,
      });

      // Crear PDF
      const pdf = new jsPDF('p', 'mm', 'a4'); // portrait orientation
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Configurar colores
      const primaryColor: [number, number, number] = [99, 102, 241]; // Azul primario
      const secondaryColor: [number, number, number] = [100, 116, 139]; // Gris azulado

      // Obtener datos del mes
      const monthYear = currentCalendarDate.toLocaleDateString('es-ES', { 
        month: 'long', 
        year: 'numeric' 
      });
      const monthName = currentCalendarDate.toLocaleDateString('es-ES', { month: 'long' });
      
      // Filtrar dolores del mes actual
      const currentMonthHeadaches = headaches.filter(headache => {
        const headacheDate = new Date(headache.date);
        return headacheDate.getMonth() === currentCalendarDate.getMonth() &&
               headacheDate.getFullYear() === currentCalendarDate.getFullYear();
      });

      // === PÁGINA 1: PORTADA Y CALENDARIO ===
      
      // Header con diseño profesional
      pdf.setFillColor(...primaryColor);
      pdf.rect(0, 0, pageWidth, 40, 'F');
      
      // Título principal
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      const titleText = 'MalDolor - Reporte Mensual';
      const titleWidth = pdf.getTextWidth(titleText);
      pdf.text(titleText, (pageWidth - titleWidth) / 2, 25);
      
      // Subtítulo con mes
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'normal');
      const subtitleText = monthYear.charAt(0).toUpperCase() + monthYear.slice(1);
      const subtitleWidth = pdf.getTextWidth(subtitleText);
      pdf.text(subtitleText, (pageWidth - subtitleWidth) / 2, 35);

      // Información del usuario
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(12);
      pdf.text(`Usuario: ${user?.name || 'Usuario'}`, 20, 55);
      pdf.text(`Fecha de generación: ${new Date().toLocaleDateString('es-ES')}`, 20, 65);
      
      // Resumen estadístico
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Resumen del mes:', 20, 85);
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(12);
      pdf.text(`• Total de episodios: ${currentMonthHeadaches.length}`, 25, 95);
      
      const intensityCount = {
        leve: currentMonthHeadaches.filter(h => h.intensity === 'leve').length,
        moderada: currentMonthHeadaches.filter(h => h.intensity === 'moderada').length,
        fuerte: currentMonthHeadaches.filter(h => h.intensity === 'fuerte').length
      };
      
      pdf.text(`• Leves: ${intensityCount.leve}`, 25, 105);
      pdf.text(`• Moderadas: ${intensityCount.moderada}`, 25, 115);
      pdf.text(`• Fuertes: ${intensityCount.fuerte}`, 25, 125);

      // Calendario centrado y más pequeño
      const calendarWidth = 150; // Reducido de 280
      const calendarHeight = (canvas.height * calendarWidth) / canvas.width;
      const calendarX = (pageWidth - calendarWidth) / 2;
      const calendarY = 140;
      
      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', calendarX, calendarY, calendarWidth, calendarHeight);

      // Leyenda centrada
      const legendY = calendarY + calendarHeight + 15;
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      const legendTitle = 'Leyenda de intensidades:';
      const legendTitleWidth = pdf.getTextWidth(legendTitle);
      pdf.text(legendTitle, (pageWidth - legendTitleWidth) / 2, legendY);
      
      // Colores de leyenda centrados
      const legendItemsY = legendY + 10;
      const legendSpacing = 45;
      const startX = (pageWidth - (3 * legendSpacing)) / 2;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      
      // Verde (leve)
      pdf.setFillColor(16, 185, 129);
      pdf.rect(startX, legendItemsY, 8, 6, 'F');
      pdf.text('Leve', startX + 12, legendItemsY + 4);
      
      // Naranja (moderada)
      pdf.setFillColor(251, 146, 60);
      pdf.rect(startX + legendSpacing, legendItemsY, 8, 6, 'F');
      pdf.text('Moderada', startX + legendSpacing + 12, legendItemsY + 4);
      
      // Rojo (fuerte)
      pdf.setFillColor(239, 68, 68);
      pdf.rect(startX + (2 * legendSpacing), legendItemsY, 8, 6, 'F');
      pdf.text('Fuerte', startX + (2 * legendSpacing) + 12, legendItemsY + 4);

      // === PÁGINA 2: DETALLES DE EPISODIOS ===
      if (currentMonthHeadaches.length > 0) {
        pdf.addPage();
        
        // Header de página 2
        pdf.setFillColor(...secondaryColor);
        pdf.rect(0, 0, pageWidth, 30, 'F');
        
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(18);
        pdf.setFont('helvetica', 'bold');
        const detailTitle = `Detalle de episodios - ${monthName}`;
        const detailTitleWidth = pdf.getTextWidth(detailTitle);
        pdf.text(detailTitle, (pageWidth - detailTitleWidth) / 2, 20);
        
        // Contenido de detalles
        pdf.setTextColor(0, 0, 0);
        let yPosition = 50;
        
        currentMonthHeadaches.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
          .forEach((headache, index) => {
            if (yPosition > 260) { // Nueva página si es necesario
              pdf.addPage();
              yPosition = 30;
            }
            
            // Fondo alternado para cada episodio
            if (index % 2 === 0) {
              pdf.setFillColor(248, 250, 252);
              pdf.rect(10, yPosition - 8, pageWidth - 20, 35, 'F');
            }
            
            // Intensidad con color
            const intensityColors: Record<string, [number, number, number]> = {
              leve: [16, 185, 129],
              moderada: [251, 146, 60],
              fuerte: [239, 68, 68]
            };
            
            pdf.setFillColor(...intensityColors[headache.intensity as keyof typeof intensityColors]);
            pdf.circle(20, yPosition, 3, 'F');
            
            // Información del episodio
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(12);
            pdf.text(`${new Date(headache.date).toLocaleDateString('es-ES')} - ${headache.intensity.toUpperCase()}`, 30, yPosition);
            
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(10);
            
            if (headache.location) {
              pdf.text(`Ubicación: ${headache.location}`, 30, yPosition + 8);
            }
            
            if (headache.medication_taken && headache.medication) {
              pdf.text(`Medicación: ${headache.medication}`, 30, yPosition + 16);
              if (headache.medication_time) {
                pdf.text(`Hora: ${headache.medication_time}`, 130, yPosition + 16);
              }
            } else {
              pdf.text(`Sin medicación`, 30, yPosition + 16);
            }
            
            yPosition += 40;
          });
      }

      // Footer en todas las páginas
      const totalPages = pdf.internal.pages.length - 1;
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(128, 128, 128);
        pdf.text(`Página ${i} de ${totalPages}`, pageWidth - 30, pageHeight - 10);
        pdf.text('Generado por MalDolor App', 20, pageHeight - 10);
      }

      // Descargar PDF
      const fileName = `reporte-maldolor-${monthYear.toLowerCase().replace(' ', '-')}.pdf`;
      pdf.save(fileName);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Error al generar el PDF');
    } finally {
      setIsExportingPDF(false);
    }
  };

  const handleExportAnnualPDF = async () => {
    setIsExportingAnnualPDF(true);
    
    try {
      // Crear PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Configurar colores
      const primaryColor: [number, number, number] = [99, 102, 241];
      const secondaryColor: [number, number, number] = [100, 116, 139];

      // Obtener año actual
      const currentYear = currentCalendarDate.getFullYear();
      
      // Filtrar dolores del año actual
      const currentYearHeadaches = headaches.filter(headache => {
        const headacheDate = new Date(headache.date);
        return headacheDate.getFullYear() === currentYear;
      });

      // === PÁGINA 1: PORTADA Y RESUMEN ANUAL ===
      
      // Header principal
      pdf.setFillColor(...primaryColor);
      pdf.rect(0, 0, pageWidth, 50, 'F');
      
      // Título principal
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(28);
      pdf.setFont('helvetica', 'bold');
      const titleText = 'MalDolor - Reporte Anual';
      const titleWidth = pdf.getTextWidth(titleText);
      pdf.text(titleText, (pageWidth - titleWidth) / 2, 25);
      
      // Año
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'normal');
      const yearText = currentYear.toString();
      const yearWidth = pdf.getTextWidth(yearText);
      pdf.text(yearText, (pageWidth - yearWidth) / 2, 42);

      // Información del paciente
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('INFORMACIÓN DEL PACIENTE', 20, 70);
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(12);
      pdf.text(`Nombre: ${user?.name || 'Usuario'}`, 20, 85);
      pdf.text(`Email: ${user?.email || 'N/A'}`, 20, 95);
      pdf.text(`Fecha de reporte: ${new Date().toLocaleDateString('es-ES')}`, 20, 105);

      // Resumen estadístico anual
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('RESUMEN ESTADÍSTICO ANUAL', 20, 125);
      
      const intensityCount = {
        leve: currentYearHeadaches.filter(h => h.intensity === 'leve').length,
        moderada: currentYearHeadaches.filter(h => h.intensity === 'moderada').length,
        fuerte: currentYearHeadaches.filter(h => h.intensity === 'fuerte').length
      };

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(12);
      pdf.text(`Total de episodios en ${currentYear}: ${currentYearHeadaches.length}`, 25, 140);
      pdf.text(`Episodios leves: ${intensityCount.leve} (${((intensityCount.leve/currentYearHeadaches.length)*100).toFixed(1)}%)`, 25, 150);
      pdf.text(`Episodios moderados: ${intensityCount.moderada} (${((intensityCount.moderada/currentYearHeadaches.length)*100).toFixed(1)}%)`, 25, 160);
      pdf.text(`Episodios fuertes: ${intensityCount.fuerte} (${((intensityCount.fuerte/currentYearHeadaches.length)*100).toFixed(1)}%)`, 25, 170);

      // Promedio mensual
      const avgPerMonth = (currentYearHeadaches.length / 12).toFixed(1);
      pdf.text(`Promedio mensual: ${avgPerMonth} episodios`, 25, 185);

      // Análisis por ubicaciones más comunes
      const locationStats = currentYearHeadaches.reduce((acc: Record<string, number>, headache) => {
        if (headache.location) {
          const locations = headache.location.split(', ');
          locations.forEach(loc => {
            acc[loc] = (acc[loc] || 0) + 1;
          });
        }
        return acc;
      }, {});

      const topLocations = Object.entries(locationStats)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 3);

      if (topLocations.length > 0) {
        pdf.setFont('helvetica', 'bold');
        pdf.text('UBICACIONES MÁS FRECUENTES', 20, 205);
        pdf.setFont('helvetica', 'normal');
        
        topLocations.forEach(([location, count], index) => {
          const percentage = ((count / currentYearHeadaches.length) * 100).toFixed(1);
          pdf.text(`${index + 1}. ${location}: ${count} episodios (${percentage}%)`, 25, 220 + (index * 10));
        });
      }

      // === PÁGINA 2: ANÁLISIS MENSUAL ===
      pdf.addPage();
      
      // Header página 2
      pdf.setFillColor(...secondaryColor);
      pdf.rect(0, 0, pageWidth, 35, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      const monthlyTitle = `Análisis Mensual - ${currentYear}`;
      const monthlyTitleWidth = pdf.getTextWidth(monthlyTitle);
      pdf.text(monthlyTitle, (pageWidth - monthlyTitleWidth) / 2, 22);

      // Tabla mensual
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      
      // Headers de tabla
      pdf.text('Mes', 20, 55);
      pdf.text('Total', 70, 55);
      pdf.text('Leves', 105, 55);
      pdf.text('Moderados', 135, 55);
      pdf.text('Fuertes', 175, 55);
      
      // Línea separadora
      pdf.setLineWidth(0.5);
      pdf.line(20, 60, 190, 60);

      pdf.setFont('helvetica', 'normal');

      const monthNames = [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
      ];

      let yPos = 70;
      monthNames.forEach((monthName, monthIndex) => {
        const monthHeadaches = currentYearHeadaches.filter(headache => {
          const headacheDate = new Date(headache.date);
          return headacheDate.getMonth() === monthIndex;
        });

        const monthIntensity = {
          leve: monthHeadaches.filter(h => h.intensity === 'leve').length,
          moderada: monthHeadaches.filter(h => h.intensity === 'moderada').length,
          fuerte: monthHeadaches.filter(h => h.intensity === 'fuerte').length
        };

        pdf.text(monthName, 20, yPos);
        pdf.text(monthHeadaches.length.toString(), 75, yPos);
        pdf.text(monthIntensity.leve.toString(), 110, yPos);
        pdf.text(monthIntensity.moderada.toString(), 150, yPos);
        pdf.text(monthIntensity.fuerte.toString(), 180, yPos);

        yPos += 15;
      });

      // === PÁGINA 3: MEDICACIÓN Y PATRONES ===
      pdf.addPage();
      
      // Header página 3
      pdf.setFillColor(...primaryColor);
      pdf.rect(0, 0, pageWidth, 35, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      const medTitle = 'Análisis de Medicación y Patrones';
      const medTitleWidth = pdf.getTextWidth(medTitle);
      pdf.text(medTitle, (pageWidth - medTitleWidth) / 2, 22);

      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');

      // Análisis de medicación
      const withMedication = currentYearHeadaches.filter(h => h.medication_taken).length;
      const withoutMedication = currentYearHeadaches.length - withMedication;

      pdf.text('USO DE MEDICACIÓN', 20, 50);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(12);
      pdf.text(`Con medicación: ${withMedication} episodios (${((withMedication/currentYearHeadaches.length)*100).toFixed(1)}%)`, 25, 65);
      pdf.text(`Sin medicación: ${withoutMedication} episodios (${((withoutMedication/currentYearHeadaches.length)*100).toFixed(1)}%)`, 25, 75);

      // Medicamentos más usados
      const medicationStats = currentYearHeadaches
        .filter(h => h.medication_taken && h.medication)
        .reduce((acc: Record<string, number>, headache) => {
          if (headache.medication) {
            acc[headache.medication] = (acc[headache.medication] || 0) + 1;
          }
          return acc;
        }, {});

      const topMedications = Object.entries(medicationStats)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5);

      if (topMedications.length > 0) {
        pdf.setFont('helvetica', 'bold');
        pdf.text('MEDICAMENTOS MÁS UTILIZADOS', 20, 95);
        pdf.setFont('helvetica', 'normal');
        
        topMedications.forEach(([medication, count], index) => {
          pdf.text(`${index + 1}. ${medication}: ${count} veces`, 25, 110 + (index * 10));
        });
      }

      // Footer en todas las páginas
      const totalPages = pdf.internal.pages.length - 1;
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(128, 128, 128);
        pdf.text(`Página ${i} de ${totalPages}`, pageWidth - 30, pageHeight - 10);
        pdf.text('Generado por MalDolor App', 20, pageHeight - 10);
      }

      // Descargar PDF
      const fileName = `reporte-anual-maldolor-${currentYear}.pdf`;
      pdf.save(fileName);
      
    } catch (error) {
      console.error('Error generating annual PDF:', error);
      setError('Error al generar el reporte anual');
    } finally {
      setIsExportingAnnualPDF(false);
    }
  };

  const closeAllModals = () => {
    setShowForm(false);
    setShowDayDetail(false);
    setEditingHeadache(null);
    setEditingTension(null);
    setEditingLloro(null);
    setSelectedDayTensions([]);
    setSelectedDayLloros([]);
    setSelectedDate('');
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="container">
        {/* Header con título */}
        <div className="dashboard-header">
          <div className="header-content">
            <h1 className="dashboard-title">
              ¡Hola, {user?.name}! 👋
            </h1>
            <p className="dashboard-subtitle">
              {currentView === 'analytics'
                ? 'Análisis avanzado y tendencias de tu salud'
                : healthMode === 'headaches'
                ? 'Gestiona tu salud y lleva un control de tus dolores de cabeza'
                : healthMode === 'tensions'
                ? 'Gestiona tu salud y lleva un control de tu tensión arterial'
                : 'Bienvenida a la llorería - Registra tus momentos emotivos'
              }
            </p>
          </div>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {/* Conditional rendering based on current view */}
        {currentView === 'analytics' ? (
          <AdvancedDashboard />
        ) : (
          <>
            {/* 1. Estadísticas dinámicas según el modo */}
            <DashboardStats
              healthMode={healthMode}
              headacheStats={stats}
              tensionStats={tensionStats}
              lloroStats={lloroStats}
            />

            {/* 2. Gráfico de tendencias */}
            <TrendChart
              healthMode={healthMode}
              headaches={headaches}
              tensions={tensions}
              lloros={lloros}
              days={30}
            />

        {/* 3. Acciones rápidas según el modo */}
        <QuickActions
          healthMode={healthMode}
          isExportingPDF={isExportingPDF}
          isExportingAnnualPDF={isExportingAnnualPDF}
          currentCalendarDate={currentCalendarDate}
          achievementStats={achievementStats}
          onNewHeadache={handleNewHeadache}
          onNewTension={handleNewTension}
          onNewLloro={handleNewLloro}
          onExportPDF={handleExportPDF}
          onExportAnnualPDF={handleExportAnnualPDF}
          onExportTensionPDF={handleExportTensionPDF}
          onExportTensionAnnualPDF={handleExportTensionAnnualPDF}
          onOpenAchievements={handleOpenAchievements}
        />

        {/* 3. Calendario con registros */}
        <div className="calendar-section">
          <h2 className="section-title">🗓️ Calendario de registros</h2>
          <div ref={calendarRef}>
            <Calendar
              mode={healthMode}
              headaches={headaches}
              tensions={tensions}
              lloros={lloros}
              onDateClick={handleDateClick}
              onCurrentDateChange={setCurrentCalendarDate}
            />
          </div>
          
          {/* Episodios/Registros del mes actual */}
          {(() => {
            // Filtrar datos según el modo
            const currentMonthData = healthMode === 'headaches' 
              ? headaches.filter(headache => {
                  const headacheDate = new Date(headache.date);
                  return headacheDate.getMonth() === currentCalendarDate.getMonth() &&
                         headacheDate.getFullYear() === currentCalendarDate.getFullYear();
                })
              : tensions.filter(tension => {
                  const tensionDate = new Date(tension.date);
                  return tensionDate.getMonth() === currentCalendarDate.getMonth() &&
                         tensionDate.getFullYear() === currentCalendarDate.getFullYear();
                });
            
            // Aplicar filtros según el modo actual
            const filteredEpisodes = healthMode === 'headaches' 
              ? (currentMonthData as Headache[]).filter(headache => {
                // Filtro por intensidad
                if (episodeFilters.intensities.length > 0 && !episodeFilters.intensities.includes(headache.intensity)) {
                  return false;
                }
                
                // Filtro por ubicación
                if (episodeFilters.locations.length > 0) {
                  const headacheLocations = headache.location ? headache.location.split(/,\s*/) : [];
                  const hasMatchingLocation = episodeFilters.locations.some(filterLocation => 
                    headacheLocations.some(headacheLocation => headacheLocation.trim() === filterLocation)
                  );
                  if (!hasMatchingLocation) return false;
                }
                
                // Filtro por fecha desde
                if (episodeFilters.dateFrom) {
                  const headacheDate = new Date(headache.date);
                  const fromDate = new Date(episodeFilters.dateFrom);
                  if (headacheDate < fromDate) return false;
                }
                
                // Filtro por fecha hasta
                if (episodeFilters.dateTo) {
                  const headacheDate = new Date(headache.date);
                  const toDate = new Date(episodeFilters.dateTo);
                  toDate.setHours(23, 59, 59, 999); // Incluir todo el día
                  if (headacheDate > toDate) return false;
                }
                
                return true;
              })
              : (currentMonthData as Tension[]).filter(tension => {
                // Para tensiones: filtros por fecha únicamente
                if (episodeFilters.dateFrom) {
                  const tensionDate = new Date(tension.date);
                  const fromDate = new Date(episodeFilters.dateFrom);
                  if (tensionDate < fromDate) return false;
                }
                
                if (episodeFilters.dateTo) {
                  const tensionDate = new Date(tension.date);
                  const toDate = new Date(episodeFilters.dateTo);
                  toDate.setHours(23, 59, 59, 999);
                  if (tensionDate > toDate) return false;
                }
                
                return true;
              });

            const currentMonthCount = currentMonthData.length;
            
            if (currentMonthCount > 0) {
              return (
                <div className="month-episodes-section">
                  <h3 className="month-episodes-title">
                    {healthMode === 'headaches' ? 'Episodios' : 'Tensiones'} de {currentCalendarDate.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
                    {filteredEpisodes.length !== currentMonthCount && (
                      <span className="filter-count"> - {filteredEpisodes.length} de {currentMonthCount} mostrados</span>
                    )}
                  </h3>
                  
                  {/* Filtros de episodios */}
                  {healthMode === 'headaches' && (
                    <EpisodeFilters
                      onFiltersChange={setEpisodeFilters}
                      totalEpisodes={currentMonthCount}
                      filteredCount={filteredEpisodes.length}
                    />
                  )}
                  
                  <div className="episodes-grid">
                    {filteredEpisodes.length > 0 ? (
                      filteredEpisodes
                        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                        .map((episode, index) => {
                          if (healthMode === 'headaches') {
                            const headache = episode as Headache;
                            return (
                              <div 
                                key={headache.id} 
                                className={`episode-card intensity-${headache.intensity}`}
                                data-offline={(headache as any).__offline ? 'true' : 'false'}
                              >
                                <div className="episode-header">
                                  <div className="episode-date">
                                    {new Date(headache.date).toLocaleDateString('es-ES', { 
                                      day: 'numeric', 
                                      month: 'short',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </div>
                                  <div className={`episode-intensity intensity-${headache.intensity}`}>
                                    {headache.intensity.toUpperCase()}
                                  </div>
                                </div>
                                
                                {headache.location && (
                                  <div className="episode-detail">
                                    <span className="detail-label">Ubicación:</span>
                                    <span className="detail-value">{formatLocationWithIcons(headache.location)}</span>
                                  </div>
                                )}
                                
                                <div className="episode-detail">
                                  <span className="detail-label">Medicación:</span>
                                  <span className="detail-value">
                                    {headache.medication_taken ? (
                                      <>
                                        {headache.medication || 'Sí'}
                                        {headache.medication_time && (
                                          <span className="medication-time"> a las {headache.medication_time}</span>
                                        )}
                                      </>
                                    ) : (
                                      'No'
                                    )}
                                  </span>
                                </div>
                              </div>
                            );
                          } else {
                            const tension = episode as Tension;
                            const getTensionCategory = (systolic: number, diastolic: number) => {
                              if (systolic < 120 && diastolic < 80) return { name: 'Normal', color: '#10b981' };
                              if (systolic >= 120 && systolic <= 129 && diastolic < 80) return { name: 'Elevada', color: '#f59e0b' };
                              if ((systolic >= 130 && systolic <= 139) || (diastolic >= 80 && diastolic <= 89)) return { name: 'Hipertensión 1', color: '#f97316' };
                              if (systolic >= 140 || diastolic >= 90) return { name: 'Hipertensión 2', color: '#ef4444' };
                              return { name: 'Indeterminada', color: '#6b7280' };
                            };
                            const category = getTensionCategory(tension.systolic, tension.diastolic);
                            
                            return (
                              <div 
                                key={tension.id} 
                                className="episode-card tension-card" 
                                style={{ borderLeftColor: category.color }}
                                data-offline={(tension as any).__offline ? 'true' : 'false'}
                              >
                                <div className="episode-header">
                                  <div className="episode-date">
                                    {(() => {
                                      // Extraer fecha sin conversión de timezone
                                      const dateMatch = tension.date?.match(/(\d{4})-(\d{2})-(\d{2})/);
                                      let dateStr = tension.date?.substring(0, 10) || '';
                                      if (dateMatch) {
                                        const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
                                        dateStr = `${parseInt(dateMatch[3])} ${months[parseInt(dateMatch[2]) - 1]}`;
                                      }
                                      const timeStr = tension.time || extractTimeFromDate(tension.date);
                                      return `${dateStr}, ${timeStr}`;
                                    })()}
                                  </div>
                                  <div className="tension-values" style={{ color: category.color }}>
                                    {tension.systolic}/{tension.diastolic}
                                  </div>
                                </div>
                                
                                <div className="episode-detail">
                                  <span className="detail-label">Categoría:</span>
                                  <span className="detail-value" style={{ color: category.color }}>
                                    {category.name}
                                  </span>
                                </div>
                                
                                {tension.pulse && (
                                  <div className="episode-detail">
                                    <span className="detail-label">Pulso:</span>
                                    <span className="detail-value">{tension.pulse} ppm</span>
                                  </div>
                                )}
                                
                                {tension.notes && (
                                  <div className="episode-detail">
                                    <span className="detail-label">Notas:</span>
                                    <span className="detail-value">{tension.notes}</span>
                                  </div>
                                )}
                              </div>
                            );
                          }
                        })
                    ) : (
                      <div className="no-episodes-message">
                        {(healthMode === 'headaches' && (episodeFilters.intensities.length > 0 || episodeFilters.locations.length > 0)) || episodeFilters.dateFrom || episodeFilters.dateTo ? (
                          <p>No se encontraron {healthMode === 'headaches' ? 'episodios' : 'tensiones'} con los filtros aplicados.</p>
                        ) : (
                          <p>No hay {healthMode === 'headaches' ? 'episodios' : 'tensiones'} registrados en este mes.</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            }
            return null;
          })()}
        </div>

        {/* Modal del formulario */}
        {showForm && (
          <div className="modal-overlay" onClick={closeAllModals}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>
                  {healthMode === 'headaches' ? (
                    editingHeadache ? '✏️ Editar Dolor de Cabeza' : '➕ Nuevo Dolor de Cabeza'
                  ) : healthMode === 'tensions' ? (
                    editingTension ? '✏️ Editar Tensión' : '🩺 Nueva Tensión'
                  ) : (
                    editingLloro ? '✏️ Editar Lloro' : '😢 Nuevo Lloro'
                  )}
                  {selectedDate && (
                    <span className="modal-date">
                      {(() => {
                        try {
                          const date = new Date(selectedDate);
                          if (isNaN(date.getTime())) return 'Fecha inválida';
                          return date.toLocaleDateString('es-ES', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          });
                        } catch (error) {
                          return 'Error en fecha';
                        }
                      })()}
                    </span>
                  )}
                </h2>
                <button
                  className="modal-close"
                  onClick={closeAllModals}
                >
                  ✕
                </button>
              </div>

              {healthMode === 'headaches' && (
                <HeadacheForm
                  onSubmit={handleHeadacheSubmit}
                  onCancel={closeAllModals}
                  initialData={editingHeadache || undefined}
                />
              )}
              {healthMode === 'tensions' && (
                <TensionForm
                  onSubmit={handleTensionSubmit}
                  onCancel={closeAllModals}
                  initialData={editingTension || undefined}
                />
              )}
              {healthMode === 'lloros' && (
                <LloroForm
                  onSubmit={handleLloroSubmit}
                  onCancel={closeAllModals}
                  initialData={editingLloro || undefined}
                />
              )}
            </div>
          </div>
        )}

        {/* Modal de detalle del día */}
        {showDayDetail && (editingHeadache || editingTension || selectedDayTensions.length > 0 || editingLloro || selectedDayLloros.length > 0) && (
          <div className="modal-overlay" onClick={closeAllModals}>
            <div className="modal-content day-detail-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>
                  📋 {(selectedDayTensions.length > 1 || selectedDayLloros.length > 1) ? 'Registros del día' : 'Detalle del registro'}
                  <span className="modal-date">
                    {(() => {
                      try {
                        const item = editingHeadache || editingTension || editingLloro || (selectedDayTensions.length > 0 ? selectedDayTensions[0] : null) || (selectedDayLloros.length > 0 ? selectedDayLloros[0] : null);
                        if (!item) return 'Sin fecha';
                        const date = new Date(item.date);
                        if (isNaN(date.getTime())) return 'Fecha inválida';
                        return date.toLocaleDateString('es-ES', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        });
                      } catch (error) {
                        return 'Error en fecha';
                      }
                    })()}
                  </span>
                </h2>
                <button
                  className="modal-close"
                  onClick={closeAllModals}
                >
                  ✕
                </button>
              </div>
              
              <div className="day-detail-content">
                {editingHeadache ? (
                  /* Contenido para dolores de cabeza */
                  <>
                    <div className="detail-item">
                      <strong>Intensidad:</strong>
                      <span className={`intensity-badge intensity-${editingHeadache.intensity}`}>
                        {editingHeadache.intensity.charAt(0).toUpperCase() + editingHeadache.intensity.slice(1)}
                      </span>
                    </div>
                    
                    {editingHeadache.location && (
                      <div className="detail-item">
                        <strong>Ubicación:</strong>
                        <span>{formatLocationWithIcons(editingHeadache.location)}</span>
                      </div>
                    )}
                    
                    <div className="detail-item">
                      <strong>Medicación:</strong>
                      <span>
                        {editingHeadache.medication_taken ? (
                          <>
                            ✅ {editingHeadache.medication || 'No especificada'}
                            {editingHeadache.medication_time && (
                              <small> a las {editingHeadache.medication_time}</small>
                            )}
                          </>
                        ) : (
                          '❌ No tomó medicación'
                        )}
                      </span>
                    </div>
                    
                    <div className="detail-item">
                      <strong>Hora del registro:</strong>
                      <span>{extractTimeFromDate(editingHeadache.date)}</span>
                    </div>
                  </>
                ) : (selectedDayTensions.length > 0) ? (
                  /* Contenido para múltiples tensiones */
                  <>
                    {selectedDayTensions.length > 1 && (
                      <div className="multiple-tensions-header">
                        <p>Se encontraron <strong>{selectedDayTensions.length}</strong> registros de tensión en este día:</p>
                      </div>
                    )}
                    
                    {selectedDayTensions.map((tension, index) => (
                      <div key={tension.id || index} className="tension-record">
                        {selectedDayTensions.length > 1 && (
                          <div className="record-number">📊 Registro #{index + 1}</div>
                        )}
                        
                        <div className="detail-item">
                          <strong>Presión Arterial:</strong>
                          <span className="tension-reading">
                            {tension.systolic}/{tension.diastolic} mmHg
                          </span>
                        </div>
                        
                        <div className="detail-item">
                          <strong>Categoría:</strong>
                          <span style={{ 
                            color: (() => {
                              const { systolic, diastolic } = tension;
                              if (systolic < 120 && diastolic < 80) return '#10b981';
                              if (systolic >= 120 && systolic <= 129 && diastolic < 80) return '#f59e0b';
                              if ((systolic >= 130 && systolic <= 139) || (diastolic >= 80 && diastolic <= 89)) return '#f97316';
                              if (systolic >= 140 || diastolic >= 90) return '#ef4444';
                              return '#6b7280';
                            })(),
                            fontWeight: 'bold'
                          }}>
                            {(() => {
                              const { systolic, diastolic } = tension;
                              if (systolic < 120 && diastolic < 80) return '🟢 Normal';
                              if (systolic >= 120 && systolic <= 129 && diastolic < 80) return '🟡 Elevada';
                              if ((systolic >= 130 && systolic <= 139) || (diastolic >= 80 && diastolic <= 89)) return '🟠 Hipertensión Grado 1';
                              if (systolic >= 140 || diastolic >= 90) return '🔴 Hipertensión Grado 2';
                              return '⚪ Indeterminada';
                            })()}
                          </span>
                        </div>
                        
                        {tension.pulse && (
                          <div className="detail-item">
                            <strong>Pulso:</strong>
                            <span>{tension.pulse} ppm</span>
                          </div>
                        )}
                        
                        {tension.notes && (
                          <div className="detail-item">
                            <strong>Notas:</strong>
                            <span>{tension.notes}</span>
                          </div>
                        )}
                        
                        <div className="detail-item">
                          <strong>Hora del registro:</strong>
                          <span>{tension.time || extractTimeFromDate(tension.date)}</span>
                        </div>

                        {/* Botones de edición/eliminación */}
                        <div className="record-actions">
                          <button
                            className="edit-record-btn"
                            onClick={() => {
                              setEditingTension(tension);
                              setShowDayDetail(false);
                              setShowForm(true);
                            }}
                          >
                            ✏️ Editar
                          </button>
                          <button
                            className="delete-record-btn"
                            onClick={() => {
                              if (window.confirm('¿Estás seguro de que quieres eliminar este registro de tensión?')) {
                                handleTensionDelete(tension.id);
                              }
                            }}
                          >
                            🗑️ Eliminar
                          </button>
                        </div>

                        {/* Separador entre registros */}
                        {selectedDayTensions.length > 1 && index < selectedDayTensions.length - 1 && (
                          <div className="record-separator"></div>
                        )}
                      </div>
                    ))}
                  </>
                ) : (selectedDayLloros.length > 0) ? (
                  /* Contenido para múltiples lloros */
                  <>
                    {selectedDayLloros.length > 1 && (
                      <div className="multiple-lloros-header">
                        <p>Se encontraron <strong>{selectedDayLloros.length}</strong> registros de lloro en este día:</p>
                      </div>
                    )}

                    {selectedDayLloros.map((lloro, index) => {
                      const getLloroIntensityColor = (intensity: string) => {
                        switch (intensity) {
                          case 'leve': return '#10b981';
                          case 'moderado': return '#f59e0b';
                          case 'fuerte': return '#ef4444';
                          default: return '#60a5fa';
                        }
                      };

                      return (
                        <div key={lloro.id || index} className="lloro-record">
                          {selectedDayLloros.length > 1 && (
                            <div className="record-number">😢 Registro #{index + 1}</div>
                          )}

                          <div className="detail-item">
                            <strong>Intensidad:</strong>
                            <span style={{
                              color: getLloroIntensityColor(lloro.intensity),
                              fontWeight: 'bold'
                            }}>
                              {lloro.intensity === 'leve' ? '😢 Leve' :
                               lloro.intensity === 'moderado' ? '😭 Moderado' :
                               '😩 Fuerte'}
                            </span>
                          </div>

                          {lloro.duration && (
                            <div className="detail-item">
                              <strong>Duración:</strong>
                              <span>{lloro.duration}</span>
                            </div>
                          )}

                          {lloro.trigger_cause && (
                            <div className="detail-item">
                              <strong>Causa:</strong>
                              <span>{lloro.trigger_cause}</span>
                            </div>
                          )}

                          {lloro.notes && (
                            <div className="detail-item">
                              <strong>Notas:</strong>
                              <span>{lloro.notes}</span>
                            </div>
                          )}

                          <div className="detail-item">
                            <strong>Hora del registro:</strong>
                            <span>{lloro.time || 'No especificada'}</span>
                          </div>

                          {/* Botones de edición/eliminación */}
                          <div className="record-actions">
                            <button
                              className="edit-record-btn"
                              onClick={() => {
                                setEditingLloro(lloro);
                                setShowDayDetail(false);
                                setShowForm(true);
                              }}
                            >
                              ✏️ Editar
                            </button>
                            <button
                              className="delete-record-btn"
                              onClick={() => {
                                if (window.confirm('¿Estás seguro de que quieres eliminar este registro de lloro?')) {
                                  handleLloroDelete(lloro.id);
                                }
                              }}
                            >
                              🗑️ Eliminar
                            </button>
                          </div>

                          {/* Separador entre registros */}
                          {selectedDayLloros.length > 1 && index < selectedDayLloros.length - 1 && (
                            <div className="record-separator"></div>
                          )}
                        </div>
                      );
                    })}
                  </>
                ) : null}
              </div>
              
              <div className="day-detail-actions">
                <button
                  className="btn btn-secondary"
                  onClick={handleEditFromDetail}
                >
                  ✏️ Editar
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => {
                    if (window.confirm('¿Estás seguro de eliminar este registro?')) {
                      if (editingHeadache) {
                        handleHeadacheDelete(editingHeadache.id);
                      } else if (editingTension) {
                        handleTensionDelete(editingTension.id);
                      } else if (editingLloro) {
                        handleLloroDelete(editingLloro.id);
                      }
                    }
                  }}
                >
                  🗑️ Eliminar
                </button>
              </div>
            </div>
          </div>
        )}
          </>
        )}
        
        {/* Modal de achievements */}
        {showAchievements && (
          <div className="modal-overlay" onClick={handleCloseAchievements}>
            <div onClick={(e) => e.stopPropagation()}>
              <AchievementsPanel onClose={handleCloseAchievements} />
            </div>
          </div>
        )}
        
        {/* Notificaciones de achievements */}
        {achievementNotifications.map((achievement, index) => (
          <AchievementNotification
            key={achievement.id}
            achievement={achievement}
            onClose={() => handleAchievementNotificationClose(achievement.id)}
            autoClose={true}
            duration={6000}
          />
        ))}
        
        {/* Indicador de estado offline */}
        <OfflineIndicator />

        {/* Setup de autenticación biométrica */}
        <BiometricSetup
          isOpen={showBiometricSetup}
          onClose={() => {
            setShowBiometricSetup(false);
            setBiometricSetupSeen(true);
          }}
          onSuccess={() => {
            setShowBiometricSetup(false);
            setBiometricSetupSeen(true);
          }}
        />
      </div>
    </div>
  );
};

export default Dashboard;